import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useRegisterMutation } from "../../redux/api/authApi";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const useParticles = (canvasRef) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, frame = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.00015 + 0.00005,
      opacity: Math.random() * 0.18 + 0.04,
      phase: Math.random() * Math.PI * 2,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      particles.forEach((p) => {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [canvasRef]);
};

const Register = () => {
  const [user, setUser] = useState({ name: "", email: "", password: "" });
  const { name, email, password } = user;
  const canvasRef = useRef(null);
  useParticles(canvasRef);

  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const [register, { isLoading, error, isSuccess }] = useRegisterMutation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isSuccess) toast.success("Successfully signed up!");
    if (isAuthenticated) navigate("/");
    if (error) toast.error(error?.data?.message);
  }, [error, navigate, isAuthenticated, isSuccess]);

  const checkPasswordStrength = (pwd) => {
    setPasswordStrength({
      hasMinLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecialChar: /[@$!%*?&#]/.test(pwd),
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
    if (name === "password") checkPasswordStrength(value);
  };

  const registerHandler = (e) => {
    e.preventDefault();
    const isPasswordValid = Object.values(passwordStrength).every(Boolean);
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements");
      return;
    }
    register({ name, email, password });
  };

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);

  const requirements = [
    { key: "hasMinLength", label: "At least 8 characters" },
    { key: "hasUpperCase", label: "One uppercase letter" },
    { key: "hasLowerCase", label: "One lowercase letter" },
    { key: "hasNumber", label: "One number" },
    { key: "hasSpecialChar", label: "One special character (@$!%*?&#)" },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <>
      <Helmet>
        <title>Sign Up — Sanguine Accent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="relative min-h-screen bg-[#f5f3ea] flex items-center justify-center px-4 py-12 overflow-hidden">

        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

        <motion.div
          className="relative z-10 max-w-md w-full space-y-6 bg-white/70 backdrop-blur-sm p-8 rounded-lg shadow-sm"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <div className="text-center space-y-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sourcesans">Join us</p>
            <h1 className="text-3xl font-playfair font-bold text-[#BC3F4A]">Sign Up</h1>
          </div>

          <div className="w-12 h-px bg-[#BC3F4A]/40 mx-auto" />

          <form onSubmit={registerHandler} className="space-y-4">
            <input
              id="name" name="name" type="text" value={name}
              onChange={onChange} placeholder="Name" required
              className="w-full px-4 py-3 border border-[#e2e1d8] rounded-md text-[#1c1917] bg-white placeholder-stone-400 font-sourcesans focus:outline-none focus:border-[#BC3F4A] transition text-sm"
            />
            <input
              id="email" name="email" type="email" value={email}
              onChange={onChange} placeholder="Email" required
              className="w-full px-4 py-3 border border-[#e2e1d8] rounded-md text-[#1c1917] bg-white placeholder-stone-400 font-sourcesans focus:outline-none focus:border-[#BC3F4A] transition text-sm"
            />
            <div>
              <input
                id="password" name="password" type="password" value={password}
                onChange={onChange} placeholder="Password" required
                className="w-full px-4 py-3 border border-[#e2e1d8] rounded-md text-[#1c1917] bg-white placeholder-stone-400 font-sourcesans focus:outline-none focus:border-[#BC3F4A] transition text-sm"
              />
              {password && (
                <div className="mt-3 p-3 bg-stone-50 rounded-md space-y-1">
                  {requirements.map(({ key, label }) => (
                    <p key={key} className={`text-xs font-sourcesans ${passwordStrength[key] ? "text-green-600" : "text-stone-400"}`}>
                      {passwordStrength[key] ? "✓" : "○"} {label}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <p className="text-red-600 text-sm font-sourcesans">{error.data?.message}</p>
            )}

            <button
              type="submit"
              disabled={isLoading || (password && !isPasswordValid)}
              className="w-full py-3 px-4 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair rounded-md transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Signing Up..." : "Sign Up"}
            </button>
          </form>

          <p className="text-center text-xs font-sourcesans text-stone-500">
            Have an account?{" "}
            <Link to="/login" className="text-[#BC3F4A] hover:text-[#a3323b] font-medium transition">
              Sign In
            </Link>
          </p>

          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sourcesans pt-2">
            Make it. Mean it.
          </p>
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
      </div>
    </>
  );
};

export default Register;